/**
 * IMDBInput Component
 * Input field for IMDB ID with validation and film lookup
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import { 
  Movie, 
  CheckCircle, 
  Error as ErrorIcon,
  Search
} from '@mui/icons-material';

const IMDBInput = ({ 
  value = '', 
  onChange, 
  onFilmLookup, 
  loading = false, 
  error = null,
  filmData = null 
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [validationError, setValidationError] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // IMDB ID validation regex
  const imdbIdRegex = /^tt\d{7,8}$/;

  // Validate IMDB ID format
  const validateIMDBId = (id) => {
    if (!id) {
      return { isValid: false, error: 'IMDB ID is required' };
    }
    
    if (!imdbIdRegex.test(id)) {
      return { 
        isValid: false, 
        error: 'Invalid IMDB ID format. Use format: tt1234567' 
      };
    }
    
    return { isValid: true, error: null };
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value.trim();
    setLocalValue(newValue);
    
    // Clear previous errors
    setValidationError(null);
    
    // Validate format
    const validation = validateIMDBId(newValue);
    if (!validation.isValid) {
      setValidationError(validation.error);
    }
    
    // Call parent onChange
    onChange(newValue);
    
    // Trigger film lookup if valid
    if (validation.isValid && newValue !== value) {
      setIsValidating(true);
      if (onFilmLookup) {
        onFilmLookup(newValue)
          .finally(() => setIsValidating(false));
      }
    }
  };

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Get input state
  const getInputState = () => {
    if (loading || isValidating) return 'loading';
    if (validationError || error) return 'error';
    if (filmData && localValue) return 'success';
    return 'default';
  };

  const inputState = getInputState();

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Enter IMDB ID
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter the IMDB ID of the film you want to assign as a comparison (e.g., tt1234567)
      </Typography>

      <TextField
        fullWidth
        label="IMDB ID"
        placeholder="tt1234567"
        value={localValue}
        onChange={handleInputChange}
        disabled={loading}
        error={!!validationError || !!error}
        helperText={
          validationError || 
          error || 
          'Format: tt followed by 7-8 digits'
        }
        InputProps={{
          startAdornment: (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              {inputState === 'loading' && <CircularProgress size={16} />}
              {inputState === 'success' && <CheckCircle sx={{ color: 'success.main', fontSize: '1rem' }} />}
              {inputState === 'error' && <ErrorIcon sx={{ color: 'error.main', fontSize: '1rem' }} />}
              {inputState === 'default' && <Search sx={{ color: 'text.secondary', fontSize: '1rem' }} />}
            </Box>
          )
        }}
        sx={{ mb: 2 }}
      />

      {/* Film Data Display */}
      {filmData && (
        <Paper sx={{ p: 2, backgroundColor: 'success.light', border: '1px solid', borderColor: 'success.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Movie sx={{ color: 'success.main', fontSize: '1.5rem' }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
                {filmData.title}
              </Typography>
              <Typography variant="caption" color="success.main">
                {filmData.filmId} • {filmData.releaseYear} • {filmData.genre}
              </Typography>
            </Box>
            <Chip
              icon={<CheckCircle />}
              label="Film Found"
              color="success"
              size="small"
            />
          </Box>
        </Paper>
      )}

      {/* Validation Status */}
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        {localValue && (
          <Chip
            label={imdbIdRegex.test(localValue) ? "Valid Format" : "Invalid Format"}
            size="small"
            color={imdbIdRegex.test(localValue) ? "success" : "error"}
            variant="outlined"
          />
        )}
        {filmData && (
          <Chip
            label="Film Verified"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  );
};

export default IMDBInput;
